// import { Avatar, AvatarFallback, AvatarImage } from "@app/components/ui/avatar";
import { Button } from "@app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@app/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@app/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Check,
  ChevronsUpDown,
  Database,
  Eye,
  FlaskConical,
  Globe,
  User,
  ArrowLeft,
  Sparkles,
  Loader2,
  Info,
  X,
  BrainCircuit,
  LifeBuoy,
  MonitorPlay,
} from "lucide-react";
import { toast } from "sonner";
import { ContactSupportDialog } from "@app/components/ContactSupportDialog";
import { Input } from "@app/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@app/components/ui/tooltip";
import { mapPatientsSelectV2CToTenantId } from "@app/utils/patient";
import { specialityShortLabel } from "@app/utils/speciality";
import { useReadonlyScope } from "@app/utils/use-readonly-scope";
import { cn } from "@app/lib/utils";
import API from "@app/services/api";
import { signOut } from "@app/utils/sign-out";
import { useNavigate, useParams, useLocation } from "react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import useMageXStore, {
  clearSearch,
  useSearchEdited,
  type NLChanges,
  type NLFilterCondition,
  type NLUnmatched,
} from "@app/zustand/store";
import { dashboardPathwayStore, usePathwayDashboard } from "@app/zustand/dashboard-pathway";
import { buildPathwayQuery } from "@app/components/dashboard-pathway/usePathway";
import { activeConditions } from "@app/components/dashboard-pathway/conditions";
import useUser from "@app/zustand/user";
import { usePreviewView } from "@app/zustand/preview-view";
import { useScope } from "@app/zustand/scope";
import type { PreviewMedecin } from "@app/zustand/preview-view";
import { usePatientSelector } from "@app/utils/use-patient-selector";
import { usePaginatedSearch } from "@app/utils/use-paginated-search";
import { fetchDatasetPickerPage } from "@app/utils/dataset-picker";
import { usePractitionerFilter } from "@app/utils/use-practitioner-filter";
import { PractitionerFilterHint } from "@app/components/PractitionerFilterHint";
import { SelectionFilterHint } from "@app/components/SelectionFilterHint";
import { isDemoEmail, switchDemoSession } from "@app/utils/demo-account";

import { Trans, useLingui } from "@lingui/macro";
import { useLocaleContext } from "@app/contexts/LocaleContext";
import type { AppLocale } from "@app/i18n";

interface ExperimentSearchItem {
  id: string;
  name: string;
  status: string;
  dataset_id: string;
}

/* The canned onboarding search. The two fields it names are load-bearing: both are dimensions of the
 * care-pathway layer, so the board can apply them — a search on columns it does not carry would
 * narrow nothing. */
const DEMO_NL_QUERY = "Patients ASA 3 opérés d'un TLIF";

const DEMO_NL_RESULT = {
  explanation: "Patients ASA 3 opérés d'un TLIF",
  parsed_filters: [
    { column: "score_asa", op: "eq", value: "ASA 3" },
    { column: "traitements_chirurgical", op: "eq", value: "TLIF" },
  ],
} as const;

/**
 * The previewed account as `/user/medecins` states it. The eye reproduces an account rather than
 * borrowing it: its speciality, its role, and whether it reviews its speciality (the shape of its pool).
 */
function previewedAccount(m: PreviewMedecin): PreviewMedecin {
  return {
    id: m.id,
    nom: m.nom,
    prenom: m.prenom,
    email: m.email,
    specialite: m.specialite ?? null,
    role: m.role ?? null,
    reviewer: m.reviewer ?? null,
  };
}

/** The patient page: switching the previewed account must leave the dossier of the previous one. */
const PATIENT_PAGE = /^\/patient\//;

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLingui();
  const { locale: currentLanguage, setAppLocale } = useLocaleContext();
  const params = useParams();
  const user = useUser((state) => state.user);
  const userRole = (user as { role?: string } | null)?.role;
  const { previewRole, previewMedecin, previewSpecialite, setPreview } =
    usePreviewView();
  const readonlyScope = useReadonlyScope();
  const [medecins, setMedecins] = useState<PreviewMedecin[]>([]);
  const [open, setOpen] = useState(false);
  const patientsSelect = useMageXStore((state) => state.patientsSelect);
  const nlSearchResult = useMageXStore((state) => state.nlSearchResult);
  const setNlSearchResult = useMageXStore((state) => state.setNlSearchResult);
  const nlEdited = useSearchEdited();
  // The search leads to saving a cohort, written under the speciality on screen: a viewer, or a
  // visitor of another speciality (PT-74), reads the board without it.
  const canSearch = !readonlyScope;
  const scope = useScope((s) => s.scope);
  const isDemoUser = isDemoEmail((user as { email?: string } | null)?.email);

  // ── Natural-language search ──────────────────────────────────────────────
  // In the store, so that the board's « Tout effacer » empties the bar too.
  const cohortQuery = useMageXStore((state) => state.nlQuery);
  const setCohortQuery = useMageXStore((state) => state.setNlQuery);
  const [nlLoading, setNlLoading] = useState(false);
  // The search in force, shared with the board's own chip so that clearing it anywhere clears it here.
  const nlExplanation = nlSearchResult?.explanation ?? null;
  const [nlError, setNlError] = useState<string | null>(null);
  const [showSupportDialog, setShowSupportDialog] = useState(false);

  // The resolved conditions go to the store; the board maps them onto its own and is where a
  // cohort is saved.
  const handleNLCohortSearch = async () => {
    if (!canSearch) return;
    const trimmed = cohortQuery.trim();
    if (!trimmed) return;
    setNlLoading(true);
    setNlError(null);
    try {
      // The board's own conditions and period go with the question: the answer is the whole set that
      // replaces them, so a question can refine them (« parmi eux… ») or start over (PT-146).
      const { periode, periodeStart, periodeEnd } = useMageXStore.getState().filterParams;
      const base = {
        conditions: activeConditions(dashboardPathwayStore.getState().conditions),
        periode: periode ?? null,
        periodeStart: periodeStart ?? null,
        periodeEnd: periodeEnd ?? null,
      };
      const res = await API.post({
        path: "/nl-search/query",
        body: {
          query: trimmed,
          scope,
          conditions: base.conditions,
          periode: periode ?? undefined,
          periodeStart: periodeStart ?? undefined,
          periodeEnd: periodeEnd ?? undefined,
        },
      });
      // Cleared while the model answered (« Tout effacer », the bar locked meanwhile): the late answer is
      // not imposed on a board the reader just emptied.
      if (useMageXStore.getState().nlQuery.trim() !== trimmed) return;
      if (res?.ok && res?.data) {
        const data = res.data as {
          explanation?: string | null;
          parsed_filters?: NLFilterCondition[];
          unmatched?: NLUnmatched[];
          changes?: NLChanges | null;
        };
        // A search that resolved nothing still has to show and be clearable.
        setNlSearchResult({
          explanation: data.explanation || trimmed,
          parsed_filters: data.parsed_filters ?? [],
          unmatched: data.unmatched ?? [],
          changes: data.changes ?? null,
          base,
        });
      } else {
        setNlError(t`La recherche a échoué. Veuillez réessayer.`);
      }
    } catch {
      setNlError(t`La recherche a échoué. Veuillez réessayer.`);
    } finally {
      setNlLoading(false);
    }
  };

  // The search's answer is the board's whole set of filters: clearing it starts the board over.
  const clearNLCohortSearch = useCallback(() => {
    setNlError(null);
    clearSearch();
  }, []);

  const handleDemoNLSearch = useCallback(() => {
    setCohortQuery(DEMO_NL_QUERY);
    setNlError(null);
    setNlSearchResult({
      explanation: DEMO_NL_RESULT.explanation,
      parsed_filters: [...DEMO_NL_RESULT.parsed_filters],
    });
  }, [setCohortQuery, setNlSearchResult]);

  const [experiments, setExperiments] = useState<ExperimentSearchItem[]>([]);

  // Patient detail page: a back arrow and the patient selector replace the search bar.
  const isPatientDetailPage =
    PATIENT_PAGE.test(location.pathname) &&
    params.tenant_id != null &&
    params.id_patient != null;

  const isDatasetRoute = location.pathname.startsWith("/datasets");
  const datasetSearch = usePaginatedSearch({
    open: isDatasetRoute && open,
    fetchPage: fetchDatasetPickerPage,
  });
  const searchValue = datasetSearch.searchValue;
  const isCatalogue = location.pathname.startsWith("/catalogue");
  const isDashboard =
    location.pathname === "/" || location.pathname === "/dashboard";

  // Œil "voir comme" : en production pour le rôle admin ; en dev visible aussi pour tester.
  // The picker on a patient page follows the dashboard's practitioner narrowing (PT-111).
  const practitionerFilter = usePractitionerFilter();

  const isDeveloper =
    userRole === "admin" || (import.meta.env.DEV && !!user?.id);

  // The demo is a session switch rather than a preview, open to every account since its data is
  // synthetic — but never from inside the demo itself.
  const canEnterDemo = !!user?.id && !isDemoUser;
  const handleEnterDemo = async () => {
    if (await switchDemoSession("enter")) return;
    toast.error(t`Impossible d'ouvrir la démo.`);
  };

  // The dashboard's selection follows the picker onto a patient page, as the practitioner does (PT-148).
  const boardConditions = usePathwayDashboard((state) => state.conditions);
  const boardPeriod = useMageXStore((state) => state.filterParams);
  const pickerSelection = useMemo(
    () =>
      buildPathwayQuery(boardConditions, {
        periode: boardPeriod.periode,
        periodeStart: boardPeriod.periodeStart,
        periodeEnd: boardPeriod.periodeEnd,
      }),
    [boardConditions, boardPeriod.periode, boardPeriod.periodeStart, boardPeriod.periodeEnd],
  );

  const {
    open: patientSelectorOpen,
    setOpen: setPatientSelectorOpen,
    searchValue: patientSearchValue,
    setSearchValue: setPatientSearchValue,
    patients: headerSelectorPatients,
    loading: headerSelectorLoading,
    hasMore: headerSelectorHasMore,
    loadMore: loadMoreHeaderPatients,
    narrowed: headerSelectorNarrowed,
  } = usePatientSelector({
    skip: isDatasetRoute || isCatalogue,
    praticien: practitionerFilter?.id,
    selection: pickerSelection,
  });

  const selectedPatientHeader = useMemo(() => {
    const source = headerSelectorPatients.length > 0 ? headerSelectorPatients : patientsSelect;
    return source?.find((p) => {
      if (!p || p.i == null) return false;
      return (
        p.i.toString() === params.id_patient &&
        mapPatientsSelectV2CToTenantId(p.c) === params.tenant_id
      );
    });
  }, [headerSelectorPatients, patientsSelect, params.id_patient, params.tenant_id]);

  // On a dataset page, the search also offers that dataset's experiments
  useEffect(() => {
    if (isDatasetRoute) {
      const datasetId = params.id;
      if (datasetId) {
        API.get({
          path: `/dataset/${datasetId}/experiments`,
          query: { limit: 100 },
        }).then((res) => {
          if (res.ok && res.data) {
            setExperiments(res.data.experiments || []);
          }
        });
      } else {
        setExperiments([]);
      }
    }
  }, [isDatasetRoute, params.id]);

  const filteredExperiments = useMemo(() => {
    if (!searchValue) return experiments.slice(0, 10);
    return experiments.filter((e) =>
      e.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [experiments, searchValue]);

  const loadMedecins = () => {
    if (medecins.length > 0) return;
    API.get({ path: "/user/medecins" }).then((res) => {
      if (res.ok && res.data?.medecins) setMedecins(res.data.medecins);
    });
  };

  // A preview persists a snapshot of the account: re-read it once per load, so a surface or a role
  // changed since the doctor was chosen applies without choosing them again.
  const previewedId = previewRole === "medecin" ? previewMedecin?.id : undefined;
  useEffect(() => {
    if (!previewedId) return;
    let cancelled = false;
    API.get({ path: "/user/medecins" }).then((res) => {
      if (cancelled || !res.ok || !res.data?.medecins) return;
      const list: PreviewMedecin[] = res.data.medecins;
      setMedecins(list);
      const fresh = list.find((m) => m.id === previewedId);
      const current = usePreviewView.getState();
      if (fresh && current.previewRole === "medecin") {
        setPreview({
          role: "medecin",
          specialite: current.previewSpecialite,
          medecin: previewedAccount(fresh),
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [previewedId, setPreview]);

  return (
    <>
    <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md shadow-sm">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Dataset Search - shown on dataset routes */}
        {isDatasetRoute ? (
          <div className="mx-auto flex flex-row items-center justify-center flex-1 max-w-2xl">
            <div className="flex-1 w-full mx-8">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between h-11 bg-white/90 hover:bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 text-slate-700"
                  >
                    <span className="truncate flex items-center gap-2">
                      <Database className="h-4 w-4 text-primary/60" />
                      <Trans>Rechercher un jeu de données ou une expérience...</Trans>
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0 shadow-xl border-slate-200">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t`Rechercher un jeu de données, une expérience...`}
                      value={searchValue}
                      onValueChange={datasetSearch.setSearchValue}
                    />
                    <CommandList onScroll={datasetSearch.onScroll}>
                      {/* Experiments first: further datasets load in at the bottom as the list scrolls. */}
                      {filteredExperiments.length > 0 && (
                        <CommandGroup heading={t`Expériences`}>
                          {filteredExperiments.map((exp) => (
                            <CommandItem
                              key={exp.id}
                              value={`experiment-${exp.id}`}
                              className="text-slate-600"
                              onSelect={() => {
                                navigate(
                                  `/datasets/${exp.dataset_id}/experiments/${exp.id}`,
                                );
                                setOpen(false);
                              }}
                            >
                              <FlaskConical className="h-4 w-4 mr-2 text-primary/70" />
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{exp.name}</span>
                                <span className="text-xs text-slate-400 ml-2">
                                  {exp.status}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {datasetSearch.loading &&
                      datasetSearch.items.length === 0 ? (
                        <div className="flex items-center justify-center py-4 text-slate-400 text-sm gap-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <Trans>Chargement...</Trans>
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>
                            <Trans>Aucun résultat trouvé.</Trans>
                          </CommandEmpty>
                          {datasetSearch.items.length > 0 && (
                            <CommandGroup heading={t`Jeux de données`}>
                              {datasetSearch.items.map((ds) => (
                                <CommandItem
                                  key={ds.id}
                                  value={`dataset-${ds.id}`}
                                  className="text-slate-600"
                                  onSelect={() => {
                                    navigate(`/datasets/${ds.id}`);
                                    setOpen(false);
                                  }}
                                >
                                  <Database className="h-4 w-4 mr-2 text-primary/70" />
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium">
                                      {ds.name}
                                    </span>
                                    {ds.description && (
                                      <p className="text-xs text-slate-400 truncate">
                                        {ds.description}
                                      </p>
                                    )}
                                  </div>
                                  <span className="text-xs text-slate-400 ml-2">
                                    {ds.file_count} <Trans>fichiers</Trans>
                                  </span>
                                </CommandItem>
                              ))}
                              {datasetSearch.loading && (
                                <div className="flex items-center justify-center py-2 text-slate-400 text-sm">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                </div>
                              )}
                            </CommandGroup>
                          )}
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ) : isPatientDetailPage ? (
          <div className="mx-auto flex flex-row items-center justify-center flex-1 max-w-2xl">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="shrink-0 text-slate-500 hover:text-primary hover:bg-primary/5"
              aria-label={t`Retour au tableau de bord`}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 w-full mx-4 max-w-md">
              <Popover
                open={patientSelectorOpen}
                onOpenChange={setPatientSelectorOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={patientSelectorOpen}
                    className="w-full justify-between h-11 bg-white/90 hover:bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 text-slate-700"
                  >
                    <span className="truncate">
                      {selectedPatientHeader ? (
                        <span>
                          {selectedPatientHeader.n}
                        </span>
                      ) : t`Sélectionner un patient`}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0 shadow-xl border-slate-200" align="center">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t`Rechercher un patient...`}
                      value={patientSearchValue}
                      onValueChange={setPatientSearchValue}
                    />
                    {practitionerFilter && (
                      <PractitionerFilterHint label={practitionerFilter.label} />
                    )}
                    {headerSelectorNarrowed && <SelectionFilterHint />}
                    <CommandList
                      onScroll={(e) => {
                        const el = e.currentTarget;
                        if (headerSelectorHasMore && !headerSelectorLoading && el.scrollHeight - el.scrollTop <= el.clientHeight + 80) {
                          loadMoreHeaderPatients();
                        }
                      }}
                    >
                      {headerSelectorLoading && headerSelectorPatients.length === 0 ? (
                        <div className="flex items-center justify-center py-4 text-slate-400 text-sm gap-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <Trans>Chargement...</Trans>
                        </div>
                      ) : (
                        <>
                          <CommandEmpty><Trans>Aucun patient trouvé.</Trans></CommandEmpty>
                          <CommandGroup>
                            {headerSelectorPatients.map((patient) => (
                              <CommandItem
                                key={`${patient.i}-${patient.c}`}
                                value={patient.n}
                                onSelect={() => {
                                  navigate(
                                    `/patient/${mapPatientsSelectV2CToTenantId(patient.c)}/${patient.i}`,
                                  );
                                  setPatientSelectorOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedPatientHeader?.i === patient.i &&
                                      selectedPatientHeader?.c === patient.c
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span>
                                  {patient.n}
                                </span>
                              </CommandItem>
                            ))}
                            {headerSelectorLoading && headerSelectorPatients.length > 0 && (
                              <div className="flex items-center justify-center py-2 text-slate-400 text-sm">
                                <Loader2 className="h-3 w-3 animate-spin" />
                              </div>
                            )}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        ) : isDashboard ? (
          /* ── Compact NLP cohort bar ── */
          <div
            data-tour="nl-search"
            className="mx-auto flex flex-row items-center justify-center flex-1 max-w-2xl gap-2"
          >
            {!nlExplanation ? (
              <>
                <div className="relative flex-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-5 w-5 rounded-full flex items-center justify-center cursor-default focus:outline-none"
                          style={{
                            background:
                              "linear-gradient(135deg, #1752B7 0%, #3b82f6 50%, #6366f1 100%)",
                          }}
                          aria-label={t`En savoir plus`}
                          tabIndex={-1}
                        >
                          <Info className="h-3 w-3 text-white" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-center">
                        <Trans>
                          Décrivez en langage naturel les patients recherchés.
                          L'IA filtre le tableau de bord selon votre requête ;
                          vous pourrez ensuite l'enregistrer comme cohorte.
                        </Trans>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    placeholder={t`Recherche IA — décrire les patients recherchés...`}
                    value={cohortQuery}
                    onChange={(e) => setCohortQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleNLCohortSearch();
                      }
                    }}
                    disabled={nlLoading || !canSearch}
                    className="pl-9 h-9 bg-white/90 border-slate-200 shadow-sm text-sm focus:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <Button
                  onClick={handleNLCohortSearch}
                  disabled={nlLoading || !canSearch || !cohortQuery.trim()}
                  size="sm"
                  className="gap-1.5 shrink-0 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {nlLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <BrainCircuit className="h-3.5 w-3.5" />
                  )}
                  <Trans>Rechercher</Trans>
                </Button>
                {nlError && (
                  <p className="text-xs text-red-600 shrink-0">{nlError}</p>
                )}
                {isDemoUser && !cohortQuery && (
                  <button
                    type="button"
                    onClick={handleDemoNLSearch}
                    className="shrink-0 text-xs text-primary/70 hover:text-primary underline underline-offset-2 transition-colors"
                  >
                    <Trans>Démo IA</Trans>
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/20 px-3 py-1.5 text-sm text-primary flex-1 min-w-0">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium truncate">{nlExplanation}</span>
                  {/* Kept rather than dropped: the question stays there to refine, and says it no
                      longer describes the board. */}
                  {nlEdited && (
                    <span className="shrink-0 text-xs text-slate-500">
                      <Trans>sélection modifiée depuis la recherche</Trans>
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearNLCohortSearch}
                  className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
                  title={t`Effacer la recherche et ses filtres`}
                  aria-label={t`Effacer la recherche et ses filtres`}
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1" />
        )}

        {/* User Menu - Always on the right */}
        <div className="flex items-center space-x-3 ml-auto">
          {isDeveloper && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 rounded-full hover:bg-primary/10 transition-colors"
                  title={t`Voir le front en tant que…`}
                  aria-label={t`Voir le front en tant qu'admin, global ou médecin`}
                >
                  <Eye className="h-4 w-4 text-slate-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 shadow-lg border-slate-200"
              >
                <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-500">
                  <Trans>Voir le front comme…</Trans>
                </div>
                <DropdownMenuItem
                  onClick={() => {
                    // Ce menu simule un RÔLE ; la barre latérale scope une SPÉCIALITÉ. Deux axes
                    // indépendants, donc revenir au rôle admin ne retire pas le périmètre posé
                    // ailleurs — il se retire depuis son propre menu.
                    setPreview(
                      previewSpecialite
                        ? { role: "admin", specialite: previewSpecialite }
                        : null,
                    );
                    if (PATIENT_PAGE.test(location.pathname))
                      navigate("/");
                  }}
                  className="cursor-pointer"
                >
                  <User className="h-4 w-4 mr-2" />
                  <Trans>Vue admin</Trans>
                  {(previewRole === null || previewRole === "admin") && (
                    <Check className="h-4 w-4 ml-auto text-primary" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setPreview({ role: "global", specialite: previewSpecialite });
                    if (PATIENT_PAGE.test(location.pathname))
                      navigate("/dashboard");
                  }}
                  className="cursor-pointer"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  <Trans>Vue globale</Trans>
                  {previewRole === "global" && (
                    <Check className="h-4 w-4 ml-auto text-primary" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    className="cursor-pointer"
                    onPointerEnter={loadMedecins}
                    onFocus={loadMedecins}
                  >
                    <User className="h-4 w-4 mr-2" />
                    <Trans>Vue médecin</Trans>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[280px] overflow-y-auto">
                    {medecins.length === 0 && (
                      <div className="px-2 py-3 text-sm text-slate-500">
                        <Trans>Chargement…</Trans>
                      </div>
                    )}
                    {medecins.map((m) => (
                      <DropdownMenuItem
                        key={m.id}
                        onClick={() => {
                          // The chosen speciality rides along like it does for every other
                          // item of this menu: it is the reader's own scope and outlives the
                          // role they are previewing. It is not what scopes THIS preview —
                          // a previewed doctor answers for their own speciality — so it is
                          // neither sent nor displayed until the preview ends.
                          setPreview({
                            role: "medecin",
                            specialite: previewSpecialite,
                            medecin: previewedAccount(m),
                          });
                          if (PATIENT_PAGE.test(location.pathname))
                            navigate("/");
                        }}
                        className="cursor-pointer"
                      >
                        <span className="flex flex-col items-start gap-0 min-w-0">
                          <span className="truncate">
                            {[m.prenom, m.nom].filter(Boolean).join(" ") || m.email}
                          </span>
                          {m.specialite ? (
                            <span className="text-3xs text-slate-500 font-normal">
                              {specialityShortLabel(m.specialite, t)}
                            </span>
                          ) : null}
                        </span>
                        {previewRole === "medecin" &&
                          previewMedecin?.id === m.id && (
                            <Check className="h-4 w-4 ml-auto text-primary shrink-0" />
                          )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canEnterDemo && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 rounded-full hover:bg-primary/10 transition-colors"
              onClick={handleEnterDemo}
              aria-label={t`Voir la démo`}
              title={t`Voir la démo`}
            >
              <MonitorPlay className="h-4 w-4 text-slate-600" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 rounded-full hover:bg-primary/10 transition-colors"
            onClick={() => setShowSupportDialog(true)}
            aria-label={t`Aide & contact`}
            title={t`Aide & contact`}
          >
            <LifeBuoy className="h-4 w-4 text-slate-600" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 rounded-full hover:bg-primary/10 transition-colors"
              >
                <MoreHorizontal className="h-4 w-4 text-slate-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 shadow-lg border-slate-200"
            >
              <div className="px-3 py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-900">
                  {user?.nom}
                </span>
              </div>
              <DropdownMenuItem
                onClick={() => signOut(() => window.location.reload())}
                className="cursor-pointer focus:bg-destructive/10 focus:text-destructive text-primary"
              >
                <Trans>Se déconnecter</Trans>
              </DropdownMenuItem>
              <div className="flex items-center justify-center gap-1 px-3 py-2 border-t border-slate-100">
                {(
                  [
                    { code: "fr", flag: "🇫🇷", label: "Français" },
                    { code: "en", flag: "🇬🇧", label: "English" },
                    { code: "de", flag: "🇩🇪", label: "Deutsch" },
                    { code: "es", flag: "🇪🇸", label: "Español" },
                  ] as const
                ).map(({ code, flag, label }) => (
                  <button
                    key={code}
                    onClick={() => setAppLocale(code as AppLocale)}
                    type="button"
                    title={label}
                    className={cn(
                      "flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-md transition-all text-center",
                      currentLanguage === code
                        ? "bg-primary/10 ring-1 ring-primary/30"
                        : "hover:bg-slate-100",
                    )}
                  >
                    <span className="text-lg leading-none">{flag}</span>
                    <span
                      className={cn(
                        "text-3xs font-medium leading-none",
                        currentLanguage === code
                          ? "text-primary"
                          : "text-slate-500",
                      )}
                    >
                      {code.toUpperCase()}
                    </span>
                  </button>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>

    <ContactSupportDialog
      open={showSupportDialog}
      onOpenChange={setShowSupportDialog}
    />
    </>
  );
}
